import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import useAdminDataStore, { selectCurrentGroupData, selectAllGroups } from "../../../store/adminData/adminData";
import useToken from "../../../hooks/useToken";
import axios from "axios";
import logger from "../../../utils/logger";
import { message } from "antd";
import {
  updateUserRole,
  getMyId,
} from "../../../services/firebaseService/endPoints/admin/members";
import { chantApi } from "../../../utils/chantApi";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "member", label: "Member" },
  { value: "ticket-scanner", label: "Ticket Scanner" },
  { value: "capo", label: "Capo" },
  { value: "leader", label: "Leader" },
  { value: "club-admin", label: "Club Admin" },
];

const useParentAdminMembers = () => {
  const { groupId } = useParams();
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const allGroups = useAdminDataStore(selectAllGroups);
  const { token } = useToken();

  // State
  const [filteredMembersList, setFilteredMembersList] = useState([]);
  const [filters, setFilters] = useState({
    group: null,
    searchTerm: "",
  });
  const [roleChanges, setRoleChanges] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Data fetching
  const fetchMembers = async (parentId, groupIds) => {
    try {
      if (!parentId) {
        throw new Error("Parent ID is required");
      }

      let groupIdsArray = groupIds || groupData?.children;

      if (groupIdsArray.length === 0) {
        throw new Error("Group ID(s) required");
      }

      const reqBody = {
        parentId: parentId,
        groupIds: groupIdsArray,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_GCP_BASE_URL}/members/parent/all`,
        JSON.stringify(reqBody),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response;
    } catch (error) {
      logger.error("Error fetching parent admin groups", error);
      return [];
    }
  };

  const { data: membersList, isLoading } = useQuery({
    queryKey: ["parentAdminMembers", groupId, token, groupData?.id],
    queryFn: () => fetchMembers(groupData?.id, groupId ? [groupId] : null),
    enabled: !!token && !!groupData?.id,
  });

  const primaryMemberList = useMemo(() => {
    let final = [];

    if (membersList?.data?.members) {
      Object.values(membersList?.data?.members || {}).forEach(
        (list) => (final = [...final, ...(list || [])])
      );
    }

    return final;
  }, [membersList?.data?.members]);

  // Filter handlers
  const resetFilters = () => {
    setFilters({
      group: null,
      searchTerm: "",
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = (members) => {
    if (!members || !Array.isArray(members)) {
      return [];
    }

    return members.filter((member) => {
      // Filter by group (null means show all groups)
      if (filters.group !== null && member.groupId !== filters.group) {
        return false;
      }

      // Filter by search term
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const name = member.name?.toLowerCase() || "";
        const email = member.email?.toLowerCase() || "";

        if (!name.includes(searchLower) && !email.includes(searchLower)) {
          return false;
        }
      }

      return true;
    });
  };

  // Role change handlers
  const handleRoleChange = (member, newRole) => {
    const changeKey = member.key || `${member.email}-${member.groupName}`;

    if (newRole === member.userRole) {
      setRoleChanges((prev) => {
        const updated = { ...prev };
        delete updated[changeKey];
        return updated;
      });
    } else {
      setRoleChanges((prev) => ({
        ...prev,
        [changeKey]: {
          userId: member.id || member.userId || member.key || member.email,
          groupId: member.groupId || member.groupName,
          newRole,
          oldRole: member.userRole,
          userName: member.name,
          groupName: member.groupName,
          changeKey,
        },
      }));
    }
  };

  const removeChange = (changeKey) => {
    setRoleChanges((prev) => {
      const updated = { ...prev };
      delete updated[changeKey];
      return updated;
    });
  };

  const handleSaveChanges = async () => {
    const changes = Object.values(roleChanges);
    const changeCount = changes.length;

    // Show confirmation dialog
    const hasFanChanges = changes.some(
      (change) => change.oldRole === "fan" || change.newRole === "fan"
    );

    const confirmMessage = hasFanChanges
      ? `You are about to update ${changeCount} member role(s). Some changes involve Fan roles which may affect member visibility. Are you sure?`
      : `You are about to update ${changeCount} member role(s). Are you sure?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsSaving(true);
    const myId = getMyId();

    try {
      // Update all roles in Firebase with notifications
      const updatePromises = changes.map(async (change) => {
        // Convert parent-admin to parentAdmin if needed
        let role = change.newRole;
        if (role === "parent-admin") {
          role = "parentAdmin";
        }

        // Update the role in Firebase
        await updateUserRole(
          change.groupId,
          change.userId,
          role,
          change.oldRole
        );

        // Send notification if not setting to member
        if (role !== "member") {
          const groupInfo = allGroups?.[change.groupId]?.data || {};
          const myUserData =
            allGroups?.[groupData?.id]?.data?.members?.[myId] || {};

          const notificationBody = {
            tit: `${change.groupName} permissions alert`,
            bdy: `${change.userName} has been granted ${role} permissions by ${myUserData?.name || "Admin"}.`,
            image: "",
            receiverDocId: "",
            topic: "",
            gName: change.groupName,
            gId: change.groupId,
            gCId: groupInfo.clubId || "",
            gLId: groupInfo.leagueId || "",
            type: "general_updates",
            timestamp: "",
            authorName: "",
            authorDocId: "",
            senderName: "",
            senderDocId: "",
            matchId: "",
            cName: "",
            cType: "",
            cId: "",
            pId: "",
          };

          try {
            await axios.post(
              `${import.meta.env.VITE_GCP_BASE_URL}/sendNotificationToAdmin`,
              JSON.stringify(notificationBody),
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
          } catch (notifError) {
            logger.error("Error sending notification:", notifError);
            // Don't fail the entire operation if notification fails
          }
        }
      });

      await Promise.all(updatePromises);

      message.success(`Successfully updated ${changeCount} member role(s)`);
      setRoleChanges({});
      setIsModalVisible(false);

      // Refetch members data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      logger.error("Error updating member roles", error);
      message.error("Failed to update some member roles. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await chantApi.post(
        `${import.meta.env.VITE_GCP_BASE_URL}/group/parent/${groupData?.id}/children`,
        { parentId: groupData?.id }
      );

      return response;
    } catch (error) {
      logger.error("Error fetching parent admin groups", error);
      return [];
    }
  };

  const { data: parentAdminGroups } = useQuery({
    queryKey: ["parentAdminGroups", groupData?.id, token],
    queryFn: fetchGroups,
    enabled: !!token && !!groupData?.id,
  });

  const groupNameOptions = useMemo(() => {
    const options = [];

    const childrenGroups = parentAdminGroups?.data?.children
      ? Object.values(parentAdminGroups?.data?.children)
      : [];

    if (Array.isArray(childrenGroups) && childrenGroups.length > 0) {
      childrenGroups.forEach((group) => {
        if (group?.id && group?.groupName) {
          options.push({
            value: group.id,
            label: group.groupName,
          });
        }
      });
    }

    return options;
  }, [parentAdminGroups?.data?.children]);

  const headerText = useMemo(() => {
    if (groupId && primaryMemberList?.length > 0) {
      return `Members - ${primaryMemberList[0]?.groupName} `;
    }
    return "Members";
  }, [groupId, primaryMemberList]);

  // Effects
  useEffect(() => {
    const filteredMembers = applyFilters(primaryMemberList);
    setFilteredMembersList(filteredMembers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, groupId]);

  useEffect(() => {
    if (primaryMemberList) {
      resetFilters();
      setFilteredMembersList(primaryMemberList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryMemberList]);

  return {
    // Data
    filteredMembersList,
    isLoading,
    groupNameOptions,
    headerText,
    ROLES,
    groupId,

    // State
    filters,
    roleChanges,
    isModalVisible,
    isSaving,

    // Handlers
    handleFilterChange,
    handleRoleChange,
    removeChange,
    handleSaveChanges,
    setIsModalVisible,
  };
};

export default useParentAdminMembers;
